import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Star, ChevronDown, Clock, MapPin, Globe, Instagram, Facebook, Store, Copy, MessageCircle } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useApp } from "@/context/AppContext";
import { api, imgUrl, siteUrl } from "@/lib/api";
import { errorKey } from "@/lib/i18n";
import { Avatar, VerifiedCheck, ExampleBadge, BusinessBadge, BuilderBadge, AnkaraStrip, useDate } from "@/components/dm/ui";
import { PostCard } from "@/components/dm/PostCard";
import { ReportButton } from "@/components/dm/ReportDialog";

export const ProfileHeader = ({ u, children }) => {
  const { t, L, hubOf, areaOf } = useApp();
  const date = useDate();
  return (
    <section className="rounded-[24px] overflow-hidden bg-[#1E4D3B] text-white" data-testid="profile-header">
      <div className="p-6 flex gap-4 items-center">
        <Avatar user={u} size={84} />
        <div className="min-w-0 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap"><h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">{u.name}</h1>{u.is_demo && <ExampleBadge />}</div>
          <div className="flex flex-wrap gap-1.5 items-center">
            {u.phone_verified && <span data-testid="verified-phone-badge" className="inline-flex items-center gap-1 rounded-full bg-[#FFFDF9] text-[#14532D] px-2.5 py-0.5 text-xs font-bold"><VerifiedCheck label="" />{t("verified_phone")}</span>}
            <BusinessBadge business={u.business} /><BuilderBadge user={u} dark />
          </div>
          <div className="text-sm text-white/80 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{L(hubOf(u.city_hub_id))}{u.area_id ? ` · ${L(areaOf(u.area_id))}` : ""}</div>
          <div className="text-xs text-white/70">{t("member_since", { date: date(u.member_since, { month: "long", year: "numeric" }) })}</div>
        </div>
      </div>
      <div className="px-6 pb-5 flex flex-wrap items-center gap-3">
        <span data-testid="vouch-trust-badge" className="inline-flex items-center gap-1.5 rounded-full bg-[#F59E0B] text-[#291E18] px-4 py-2 font-extrabold"><Star className="w-4 h-4 fill-[#291E18]" />{u.vouch_count === 1 ? t("vouches_1") : t("vouches_n", { n: u.vouch_count })}</span>
        {children}
      </div>
      <AnkaraStrip />
    </section>
  );
};

export const BusinessSection = ({ b }) => {
  const { t, L, hubOf, cat } = useApp();
  if (!b) return null;
  const link = (href, I, label, tid) => href && <a href={href.startsWith("http") ? href : `https://${href}`} target="_blank" rel="noreferrer" data-testid={tid} className="press inline-flex items-center gap-1.5 min-h-[40px] px-3 rounded-full bg-[#F3EDE4] text-sm font-semibold"><I className="w-4 h-4" />{label}</a>;
  return (
    <section data-testid="business-section" className="rounded-[20px] bg-[#FFFDF9] border border-[#E8E2D9] overflow-hidden">
      {(b.cover_url || b.logo_url) && (
        <div className="relative">
          {b.cover_url ? <img data-testid="shop-cover" src={imgUrl(b.cover_url)} alt="" loading="lazy" className="w-full aspect-[3/1] object-cover" />
            : <div className="w-full aspect-[4/1] bg-[#1E4D3B] ankara-dots" />}
          {b.logo_url && <img data-testid="shop-logo" src={imgUrl(b.logo_url)} alt={b.business_name} className="absolute -bottom-10 left-5 w-24 h-24 rounded-2xl object-cover ring-4 ring-[#FFFDF9] bg-[#FFFDF9] shadow-lg" />}
        </div>
      )}
      <div className={`p-5 space-y-3 ${b.logo_url ? "pt-14" : ""}`}>
        <div className="flex items-center gap-2 flex-wrap"><h2 className="text-lg font-extrabold">{b.business_name}</h2><BusinessBadge business={b} /></div>
        {b.category_id && <div className="text-sm font-semibold text-[#C2410C]">{L(cat(b.category_id))}</div>}
        {b.description && <p className="text-base">{b.description}</p>}
        {b.hours && <div className="text-sm flex items-center gap-1.5"><Clock className="w-4 h-4" />{b.hours}</div>}
        {b.service_areas?.length > 0 && <div className="text-sm flex items-center gap-1.5"><MapPin className="w-4 h-4" />{b.service_areas.map((h) => L(hubOf(h)) || h).join(" · ")}</div>}
        <div className="flex flex-wrap gap-2">{link(b.instagram_url, Instagram, "Instagram", "biz-instagram")}{link(b.facebook_url, Facebook, "Facebook", "biz-facebook")}{link(b.website_url, Globe, t("website"), "biz-website")}</div>
        {b.slug && <ShopLinkBox slug={b.slug} />}
      </div>
    </section>
  );
};

export const ShopLinkBox = ({ slug, testid = "shop-link" }) => {
  const { t } = useApp();
  const url = `${siteUrl()}/${slug}`;
  const copy = async () => { try { await navigator.clipboard.writeText(url); } catch { /* ignore */ } toast.success(t("link_copied")); };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-2xl bg-[#FEF3C7]/60 p-2 pl-4" data-testid={testid}>
      <span className="font-mono font-bold text-sm text-[#92400E] break-all flex-1 min-w-[160px]">{url.replace(/^https?:\/\//, "")}</span>
      <button data-testid={`${testid}-copy`} onClick={copy} className="press min-h-[44px] px-4 rounded-full bg-[#FFFDF9] text-sm font-bold inline-flex items-center gap-1.5"><Copy className="w-4 h-4" />{t("copy_link")}</button>
      <a data-testid={`${testid}-whatsapp`} href={`https://wa.me/?text=${encodeURIComponent(`${t("shop_share_msg")} ${url}`)}`} target="_blank" rel="noreferrer" className="press min-h-[44px] px-4 rounded-full bg-[#128C7E] text-white text-sm font-bold inline-flex items-center gap-1.5"><MessageCircle className="w-4 h-4" />WhatsApp</a>
    </div>
  );
};

const VouchButton = ({ u, reload }) => {
  const { t, user, requireVerified } = useApp();
  if (user?.id === u.id) return null;
  const disabled = user && user.phone_verified && !u.has_contact && !u.has_vouched;
  const click = async () => {
    if (!requireVerified()) return;
    try {
      if (u.has_vouched) await api.delete(`/users/${u.id}/vouch`);
      else { await api.post(`/users/${u.id}/vouch`); toast.success(t("vouch_done")); }
      reload();
    } catch (e) { toast.error(t(errorKey(e) === "contact_first" ? "vouch_need_contact" : errorKey(e))); }
  };
  return (
    <div className="flex flex-col gap-1">
      <button data-testid="vouch-button" disabled={disabled} onClick={click} aria-pressed={u.has_vouched}
        className={`press min-h-[48px] px-5 rounded-full font-extrabold inline-flex items-center gap-2 disabled:opacity-50 ${u.has_vouched ? "bg-[#FFFDF9] text-[#1E4D3B]" : "border-2 border-[#F59E0B] text-[#FDE68A]"}`}>
        <Star className="w-4 h-4" />{u.has_vouched ? t("vouched") : t("vouch")}
      </button>
      {disabled && <span data-testid="vouch-disabled-reason" className="text-xs text-white/80 max-w-[220px]">{t("vouch_need_contact")}</span>}
    </div>
  );
};

export default function PublicProfile({ userId, shopFirst }) {
  const params = useParams();
  const id = userId || params.id;
  const { t, user } = useApp();
  const [u, setU] = useState(null);
  const load = () => api.get(`/users/${id}`).then((r) => setU(r.data)).catch(() => setU(false));
  useEffect(() => { load(); }, [id, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (u && !shopFirst && window.location.hash === "#shop") document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" }); }, [u]); // eslint-disable-line react-hooks/exhaustive-deps
  if (u === false) return <div className="p-8 text-center font-bold">404</div>;
  if (!u) return <div className="max-w-4xl mx-auto p-4"><div className="skeleton h-48 rounded-[24px]" /></div>;
  const shop = u.business_full?.enabled ? u.active_posts.filter((p) => p.in_shop) : [];
  const rest = u.active_posts.filter((p) => !shop.includes(p));
  const grid = (list) => <div className="grid grid-cols-[minmax(0,1fr)] gap-5 sm:grid-cols-2 lg:grid-cols-3">{list.map((p) => <PostCard key={p.id} p={p} />)}</div>;
  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6" data-testid="public-profile">
      <ProfileHeader u={u}><VouchButton u={u} reload={load} /></ProfileHeader>
      <BusinessSection b={u.business_full} />
      {shop.length > 0 && (
        <section id="shop" className="space-y-3 scroll-mt-20" data-testid="shop-section">
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <h2 className="text-base md:text-lg font-extrabold inline-flex items-center gap-2"><Store className="w-5 h-5 text-[#C2410C]" />{t("shop_title")} · {u.business_full.business_name}</h2>
            <span data-testid="shop-count" className="text-sm font-semibold text-[#6B5E55]">{shop.length === 1 ? t("shop_item_1") : t("shop_items", { n: shop.length })}</span>
          </div>
          {grid(shop)}
        </section>
      )}
      {(rest.length > 0 || shop.length === 0) && (
        <section className="space-y-3">
          <h2 className="text-base md:text-lg font-extrabold">{shop.length ? t("shop_other_posts") : t("active_posts")}</h2>
          {rest.length ? grid(rest) : <p className="text-[#6B5E55]">{t("no_posts_yet")}</p>}
        </section>
      )}
      {u.closed_posts.length > 0 && (
        <Collapsible>
          <CollapsibleTrigger data-testid="closed-posts-toggle" className="press flex items-center gap-2 min-h-[48px] px-4 rounded-full bg-[#F3EDE4] font-bold">{t("closed_posts")} ({u.closed_posts.length})<ChevronDown className="w-4 h-4" /></CollapsibleTrigger>
          <CollapsibleContent className="pt-4"><div className="grid grid-cols-[minmax(0,1fr)] gap-5 sm:grid-cols-2 lg:grid-cols-3">{u.closed_posts.map((p) => <PostCard key={p.id} p={p} />)}</div></CollapsibleContent>
        </Collapsible>
      )}
      <ReportButton itemType="profile" itemId={u.id} testid="report-profile-button" label={t("report_profile")} />
    </div>
  );
}
